import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import '@fortawesome/fontawesome-free/css/all.min.css';
import rocketbg from '../../assets/images/banners/rocket_bg.png';
import rocket from '../../assets/images/banners/rocket.png';
import salientIso from '../../assets/images/logos/salient-isotipo-blanco.png';
import sunwattLogo from '../../assets/images/partners/sunwatt.png';
import cintegraLogo from '../../assets/images/partners/cintegra.png';
import { useLanguage } from '../../i18n/LanguageContext';

// Metadata (no traducible) de las tarjetas-teaser: ícono y ruta.
// El texto (title/description/features) viene de t('home.services') por índice.
const serviceMeta = [
    { icon: 'fa-network-wired', to: '/tecnologia/redes' },
    { icon: 'fa-video', to: '/tecnologia/cctv' },
    { icon: 'fa-laptop-code', to: '/tecnologia/software' },
    { icon: 'fa-bullhorn', to: '/marketing' },
    { icon: 'fa-solar-panel', to: '/otros-servicios' },
];

const ProcessSection = () => {
    const { t } = useLanguage();
    const steps = t('home.process.steps');
    return (
        <section className="py-20 bg-white">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="text-center mb-16">
                    <h2 className="text-4xl font-bold text-gray-900 mb-4">{t('home.process.title')}</h2>
                    <div className="w-24 h-1 bg-salient-green mx-auto mb-8 rounded-full"></div>
                    <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                        {t('home.process.subtitle')}
                    </p>
                </div>

                <div className="relative grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-10 md:gap-8">
                    {/* Línea conectora sutil (solo escritorio) */}
                    <div className="hidden md:block absolute top-7 left-[12.5%] right-[12.5%] h-px bg-gray-200"></div>
                    {steps.map((process, index) => (
                        <div key={index} className="relative text-center">
                            <div className="w-14 h-14 mx-auto mb-5 bg-salient-primary rounded-full flex items-center justify-center text-white text-lg font-bold ring-8 ring-white">
                                {String(index + 1).padStart(2, '0')}
                            </div>
                            <h3 className="text-lg font-semibold text-gray-900 mb-2">{process.title}</h3>
                            <p className="text-gray-600 leading-relaxed text-sm">{process.description}</p>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
};

// Socios comerciales. Para agregar un socio: añade { name, href, logo }
// e importa su logo (versión blanca/clara para que resalte sobre el fondo azul).
const partners = [
    { name: 'Sunwatt', href: 'https://sunwatt.ar-energia.com.mx/', logo: sunwattLogo },
    { name: 'Cintegra', href: 'https://cintegra.mx/', logo: cintegraLogo },
];

const PartnersSection = () => {
    const { t } = useLanguage();
    return (
    <section className="bg-navy-gradient py-16 relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center mb-10">
            <h2 className="text-white font-bold text-4xl mb-4">{t('home.partners.title')}</h2>
            <span className="title-underline mx-auto"></span>
            <p className="text-slate-300 mt-4 max-w-2xl mx-auto">
                {t('home.partners.subtitle')}
            </p>
        </div>
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex flex-col sm:flex-row items-center justify-center gap-6 sm:gap-10">
                {partners.map((p) => (
                    <a
                        key={p.name}
                        href={p.href}
                        target="_blank"
                        rel="noreferrer"
                        aria-label={p.name}
                        className="group flex items-center justify-center w-40 sm:w-48 px-6 py-5 rounded-xl bg-white/5 border border-white/10 hover:bg-white/10 hover:border-salient-green/40 transition-all duration-300"
                    >
                        <img
                            src={p.logo}
                            alt={p.name}
                            className="h-12 md:h-14 w-auto object-contain opacity-90 group-hover:opacity-100 group-hover:scale-105 transition-all duration-300"
                        />
                    </a>
                ))}
            </div>
        </div>
    </section>
    );
};

/* Testimonios — comentado temporalmente (reemplazado por "Socios comerciales").
   Reactivar cuando haya reseñas reales de clientes.
const TestimonialsSection = () => {
    const testimonios = [
        { name: 'María González', company: 'Restaurante La Cocina', text: 'Salient transformó completamente nuestro marketing digital. En 3 meses aumentamos nuestras ventas en un 40%.', rating: 5 },
        { name: 'Carlos Mendoza', company: 'Empresa Industrial', text: 'La instalación de paneles solares redujo nuestra factura eléctrica en un 70%. Excelente inversión y servicio profesional.', rating: 5 },
        { name: 'Ana Rodríguez', company: 'Oficina Contable', text: 'Su equipo de redes nos dio una solución integral. Ahora tenemos mejor conectividad y seguridad total.', rating: 5 }
    ];
    const initials = (name) => name.split(' ').map((n) => n[0]).slice(0, 2).join('');

    return (
        <section className="py-20 bg-gray-50">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="text-center mb-16">
                    <h2 className="text-4xl font-bold text-gray-900 mb-4">Lo que dicen nuestros clientes</h2>
                    <div className="w-24 h-1 bg-salient-green mx-auto rounded-full"></div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                    {testimonios.map((testimonial, index) => (
                        <div key={index} className="bg-white rounded-2xl border border-gray-100 shadow-sm p-8 flex flex-col transition-all duration-200 hover:shadow-lg hover:-translate-y-1">
                            <div className="flex gap-1 mb-5 text-salient-secondary">
                                {[...Array(testimonial.rating)].map((_, i) => (
                                    <i key={i} className="fas fa-star text-sm"></i>
                                ))}
                            </div>
                            <p className="text-gray-700 mb-8 leading-relaxed flex-grow">{testimonial.text}</p>
                            <div className="flex items-center gap-3 pt-5 border-t border-gray-100">
                                <div className="w-11 h-11 rounded-full bg-salient-primary/10 text-salient-primary flex items-center justify-center font-semibold">
                                    {initials(testimonial.name)}
                                </div>
                                <div>
                                    <p className="font-semibold text-gray-900 leading-tight">{testimonial.name}</p>
                                    <p className="text-gray-500 text-sm">{testimonial.company}</p>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
};
*/

const Solutions = () => {
    const { t } = useLanguage();
    const [startAnimation, setStartAnimation] = useState(false);

    // Combina metadata (ícono/ruta) con el texto traducido por índice.
    const servicios = serviceMeta.map((meta, i) => ({ ...meta, ...t('home.services')[i] }));

    useEffect(() => {
        const animationTimer = setTimeout(() => setStartAnimation(true), 800);
        return () => clearTimeout(animationTimer);
    }, []);

    return (
        <>
        {/* Hero */}
        <section className="solutions-hero pt-20 relative overflow-hidden"
        style={{
            backgroundImage: `url(${rocketbg})`,
            backgroundPosition: 'center top',
            backgroundSize: 'cover',
            backgroundRepeat: 'no-repeat'
        }}>
            <div className="absolute inset-0 bg-white/30 z-0"></div>

            {/* Estela / escape anclado a la tobera (misma animación de vuelo que el cohete, detrás de él) */}
            <div
                className={`absolute ${ startAnimation ? 'rocket-animation' : '' }`}
                style={{ left: '20%', bottom: '6%', width: 'min(32vw, 20rem)', height: 'min(48vw, 30rem)', transformOrigin: 'center bottom', zIndex: 1, opacity: 0, pointerEvents: 'none' }}
            >
                {/* Halo difuso exterior */}
                <div
                    className={ startAnimation ? 'flame-flicker-slow' : '' }
                    style={{
                        position: 'absolute', left: '50%', top: '75%', transform: 'translateX(-50%)', transformOrigin: 'top center',
                        width: '5vw', height: '12vw',
                        background: 'radial-gradient(ellipse at top, rgba(130,205,255,0.45), rgba(130,205,255,0.12) 45%, rgba(130,205,255,0) 72%)',
                        filter: 'blur(7px)'
                    }}
                />
                {/* Núcleo de la estela */}
                <div
                    className={ startAnimation ? 'flame-flicker' : '' }
                    style={{
                        position: 'absolute', left: '50%', top: '76%', transform: 'translateX(-50%)', transformOrigin: 'top center',
                        width: '2.2vw', height: '8vw',
                        background: 'linear-gradient(to bottom, rgba(255,255,255,0.85), rgba(140,205,255,0.7) 30%, rgba(0,150,255,0.4) 70%, rgba(0,150,255,0))',
                        borderRadius: '50% 50% 50% 50% / 35% 35% 65% 65%',
                        filter: 'blur(3px)'
                    }}
                />
            </div>

            <img
                src={rocket}
                alt="Rocket"
                className={`absolute ${ startAnimation ? 'rocket-animation' : '' }`}
                style={{ left: '20%', bottom: '6%', height: 'min(48vw, 30rem)', transformOrigin: 'center bottom', zIndex: 2, opacity: 0 }}
            />

            <div className="hero-mkt flex flex-row justify-around items-center p-10 pb-0 contain relative z-10">
                <div className="flex flex-col justify-start items-center gap-4" data-aos="fade-right">
                    <h1 className="text-white font-bold text-center text-5xl md:text-7xl lg:text-8xl leading-tight">{t('home.hero.titleLine1')}<br/>{t('home.hero.titleLine2')}</h1>
                    <p className="text-white w-2/3 text-center">{t('home.hero.subtitle')}</p>
                    <Link to='/contact' className='btn btn-primary mt-8'>{t('common.solicitarAsesoria')}</Link>
                </div>
            </div>
        </section>

        {/* Servicios Principales (teasers) — alineado al Manual de Identidad Corporativa */}
        <section
            className="services-section relative overflow-hidden py-24"
            style={{
                background: 'linear-gradient(180deg, #0d2745 0%, #0a1f38 55%, #071829 100%)',
                fontFamily: "'Inter', sans-serif",
            }}
        >
            {/* Marca de agua: isotipo salamandra */}
            <img
                src={salientIso}
                alt=""
                aria-hidden="true"
                className="pointer-events-none select-none absolute right-0 top-1/2 -translate-y-1/2 h-[125%] w-auto opacity-[0.05] hidden md:block"
            />
            {/* Patrón de puntos (decorativo, esquina superior izquierda) */}
            <div
                className="pointer-events-none absolute top-10 left-6 w-36 h-28 opacity-40"
                style={{
                    backgroundImage: 'radial-gradient(rgba(153,225,122,0.35) 1.3px, transparent 1.3px)',
                    backgroundSize: '14px 14px',
                    maskImage: 'linear-gradient(135deg, #000, transparent 75%)',
                    WebkitMaskImage: 'linear-gradient(135deg, #000, transparent 75%)',
                }}
            ></div>

            <div className="contain relative z-10">
                {/* Encabezado enmarcado */}
                <div
                    className="rounded-3xl border border-white/10 bg-white/[0.03] px-8 py-10 md:px-12 md:py-12 mb-14 max-w-4xl"
                    data-aos="fade-up"
                >
                    <div className="flex items-center gap-4 mb-5">
                        <span
                            className="w-12 h-12 flex items-center justify-center rounded-full text-xl"
                            style={{ backgroundColor: '#085D96', color: '#99E17A' }}
                        >
                            <i className="fas fa-briefcase"></i>
                        </span>
                        <h2 className="text-white font-bold text-4xl md:text-5xl" style={{ fontFamily: "'Montserrat', sans-serif" }}>
                            {t('home.servicesHeader.title')}
                        </h2>
                    </div>
                    <div className="h-1.5 w-24 rounded-full mb-6" style={{ backgroundColor: '#99E17A' }}></div>
                    <p className="text-slate-300 text-lg leading-relaxed">
                        {t('home.servicesHeader.intro')}
                    </p>
                </div>

                {/* Tarjetas de servicio */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-6">
                    {servicios.map((service, index) => (
                        <div
                            key={index}
                            className="group relative overflow-hidden rounded-2xl border border-white/10 bg-white/[0.04] p-7 flex flex-col transition-all duration-300 ease-out hover:-translate-y-2 hover:bg-white/[0.07] hover:border-[#99E17A]/40 hover:shadow-2xl"
                            data-aos="fade-up"
                            data-aos-delay={index * 100}
                        >
                            {/* Número de fondo */}
                            <span
                                className="absolute top-4 right-5 font-bold text-5xl leading-none select-none text-white/[0.06]"
                                style={{ fontFamily: "'Montserrat', sans-serif" }}
                            >
                                {String(index + 1).padStart(2, '0')}
                            </span>

                            {/* Ícono en círculo */}
                            <div
                                className="w-14 h-14 mb-6 flex items-center justify-center rounded-full text-2xl border transition-transform duration-300 ease-out group-hover:scale-110"
                                style={{ backgroundColor: 'rgba(153,225,122,0.10)', borderColor: 'rgba(153,225,122,0.30)', color: '#99E17A' }}
                            >
                                <i className={`fas ${service.icon}`}></i>
                            </div>

                            <h3 className="text-white font-semibold text-xl mb-3 relative z-10" style={{ fontFamily: "'Montserrat', sans-serif" }}>
                                {service.title}
                            </h3>
                            <p className="text-slate-400 text-sm leading-relaxed mb-6 relative z-10">{service.description}</p>

                            <ul className="space-y-2.5 mb-7 relative z-10">
                                {service.features.map((feature, i) => (
                                    <li key={i} className="flex items-center text-slate-200 text-sm">
                                        <i className="fas fa-check text-xs mr-3" style={{ color: '#99E17A' }}></i>
                                        {feature}
                                    </li>
                                ))}
                            </ul>

                            <Link
                                to={service.to}
                                className="mt-auto relative z-10 inline-flex items-center justify-center gap-2 w-full rounded-lg border border-white/20 px-4 py-2.5 text-sm font-semibold text-white transition-all duration-200 hover:border-[#99E17A] hover:text-[#99E17A]"
                            >
                                {t('common.conocerMas')}
                                <i className="fas fa-arrow-right text-xs transition-transform duration-200 group-hover:translate-x-1"></i>
                            </Link>
                        </div>
                    ))}
                </div>
            </div>
        </section>

        <ProcessSection />
        {/* <TestimonialsSection /> — comentado temporalmente, reemplazado por Socios comerciales */}
        <PartnersSection />

        {/* Descarga de portafolio */}
        <section className="bg-white py-16">
            <div className="contain">
                <div className="bg-navy-gradient relative overflow-hidden rounded-3xl px-8 py-12 md:px-14 flex flex-col md:flex-row items-center justify-between gap-8" data-aos="zoom-in">
                    <div className="absolute -top-20 -right-20 w-72 h-72 bg-salient-green/15 rounded-full blur-3xl"></div>
                    <div className="dot-grid pointer-events-none absolute top-6 left-6 w-28 h-20 opacity-40" style={{ maskImage: 'linear-gradient(135deg,#000,transparent 75%)', WebkitMaskImage: 'linear-gradient(135deg,#000,transparent 75%)' }}></div>
                    <div className="relative z-10 text-center md:text-left">
                        <h2 className="text-white font-bold text-3xl md:text-4xl mb-3">{t('home.portfolio.title')}</h2>
                        <span className="title-underline mx-auto md:mx-0 mb-4"></span>
                        <p className="text-slate-300 text-lg max-w-xl">
                            {t('home.portfolio.text')}
                        </p>
                    </div>
                    <a
                        href="/portafolio-salient.pdf"
                        download="Portafolio-Salient.pdf"
                        className="btn btn-accent relative z-10 whitespace-nowrap"
                    >
                        <i className="fas fa-file-pdf"></i>
                        {t('home.portfolio.button')}
                    </a>
                </div>
            </div>
        </section>

        {/* CTA final */}
        <section className="bg-navy-gradient relative overflow-hidden flex flex-col justify-center items-center px-6 py-20 text-center" data-aos="zoom-in">
            <div className="absolute -top-24 -right-24 w-80 h-80 bg-salient-green/10 rounded-full blur-3xl"></div>
            <div className="relative z-10 flex flex-col items-center">
                <h3 className="text-white font-bold text-2xl md:text-3xl mb-4 max-w-2xl">{t('home.finalCta.title')}</h3>
                <span className="title-underline mb-8"></span>
                <Link to='/contact' className="btn btn-accent">{t('common.solicitarAsesoria')}</Link>
            </div>
        </section>
        </>
    );
};

export default Solutions;
